import React, { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Send, Mic, Paperclip, KeyRound, Plus, ArrowLeft, Square, Eye, EyeOff, Copy, FileText, Building2,
  Check, CheckCheck, Pin, Archive, MoreVertical, Smile, ArchiveRestore, Users, BarChart3, UserPlus, X, Globe, ExternalLink,
} from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";
import { useCompany } from "../context/CompanyContext";
import { Avatar } from "../components/Avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const REACTIONS = ["👍", "❤️", "😂", "🙏", "😮", "✅"];
const FILTERS = [{ id: "all", label: "Toutes" }, { id: "unread", label: "Non lues" }, { id: "pinned", label: "Favoris" }];

export default function ChatPage() {
  const { user } = useAuth();
  const { subscribe, refreshSummary, events } = useRealtime();
  const { selectedId } = useCompany();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newMode, setNewMode] = useState("direct");
  const [groupName, setGroupName] = useState("");
  const [groupMembers, setGroupMembers] = useState([]);
  const [pollDialog, setPollDialog] = useState(false);
  const [poll, setPoll] = useState({ question: "", options: ["", ""], multi: false });
  const [users, setUsers] = useState([]);
  const [pwdDialog, setPwdDialog] = useState(false);
  const [pwdValue, setPwdValue] = useState("");
  const [pwdLogin, setPwdLogin] = useState("");
  const [pwdLink, setPwdLink] = useState("");
  const [filter, setFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [menuFor, setMenuFor] = useState(null);
  const [typingFrom, setTypingFrom] = useState(null);
  const fileRef = useRef();
  const scrollRef = useRef();
  const bottomRef = useRef();
  const typingTimer = useRef();
  const typingPing = useRef(0);
  const audioCtx = useRef(null);

  const beep = useCallback(() => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext; if (!AC) return;
      const ctx = audioCtx.current || (audioCtx.current = new AC());
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination); o.type = "sine"; o.frequency.value = 720;
      g.gain.setValueAtTime(0.0001, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.1, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
      o.start(); o.stop(ctx.currentTime + 0.31);
    } catch (e) {}
  }, []);

  const pingTyping = useCallback(() => {
    if (!active) return;
    const now = Date.now();
    if (now - typingPing.current < 3000) return;
    typingPing.current = now;
    api.post(`/conversations/${active.id}/typing`).catch(() => {});
  }, [active]);

  const loadConvs = useCallback(async () => {
    const url = selectedId && selectedId !== "all" ? `/conversations?company_id=${selectedId}` : "/conversations";
    const { data } = await api.get(url); setConvs(data);
  }, [selectedId]);

  useEffect(() => { loadConvs(); api.get("/users").then((r) => setUsers(r.data)); }, [loadConvs]);
  useEffect(() => { loadConvs(); }, [events, loadConvs]);

  const openConv = useCallback(async (c) => {
    setActive(c);
    const { data } = await api.get(`/conversations/${c.id}/messages`);
    setMessages(data);
    refreshSummary();
    loadConvs();
  }, [refreshSummary, loadConvs]);

  // realtime
  useEffect(() => {
    const unsub = subscribe((msg) => {
      if (msg.type === "new_message") { try { beep(); } catch (e) {} }
      if (msg.type === "typing" && active && msg.conversation_id === active.id) {
        setTypingFrom(msg.name || "Quelqu'un");
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => setTypingFrom(null), 4000);
        return;
      }
      if (!active) return;
      if (msg.type === "new_message" && msg.conversation_id === active.id) {
        setTypingFrom(null);
        setMessages((prev) => [...prev, msg.message]);
        api.get(`/conversations/${active.id}/messages`).then(() => refreshSummary());
      } else if (msg.type === "messages_read" && msg.conversation_id === active.id) {
        setMessages((prev) => prev.map((mm) => mm.sender_id === user.id
          ? { ...mm, read_by: Array.from(new Set([...(mm.read_by || []), msg.reader_id])) } : mm));
      } else if (msg.type === "message_reaction" && msg.conversation_id === active.id) {
        setMessages((prev) => prev.map((mm) => mm.id === msg.message_id ? { ...mm, reactions: msg.reactions } : mm));
      } else if (msg.type === "poll_update" && msg.conversation_id === active.id) {
        setMessages((prev) => prev.map((mm) => mm.id === msg.message_id ? { ...mm, poll: msg.poll } : mm));
      }
    });
    return unsub;
  }, [subscribe, active, refreshSummary, user]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (payload) => {
    if (!active) return;
    const { data } = await api.post(`/conversations/${active.id}/messages`, payload);
    setMessages((prev) => [...prev, data]);
    loadConvs();
  };
  const sendText = async () => {
    if (!text.trim()) return;
    const t = text; setText("");
    await send({ type: "text", text: t });
  };
  const startConv = async (u) => {
    const company_id = selectedId && selectedId !== "all" ? selectedId : undefined;
    const { data } = await api.post("/conversations", { user_id: u.id, company_id, source: "chat" });
    setShowNew(false);
    await loadConvs();
    openConv(data);
  };
  const toggleGroupMember = (id) => {
    setGroupMembers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const createGroup = async () => {
    if (!groupName.trim()) { toast.error("Nom du groupe requis"); return; }
    if (groupMembers.length < 1) { toast.error("Ajoutez au moins un membre"); return; }
    const company_id = selectedId && selectedId !== "all" ? selectedId : undefined;
    const { data } = await api.post("/conversations/group", { name: groupName, participant_ids: groupMembers, company_id });
    setShowNew(false); setGroupName(""); setGroupMembers([]); setNewMode("direct");
    await loadConvs();
    openConv(data);
    toast.success("Groupe créé");
  };
  const sendPoll = async () => {
    const opts = poll.options.map((o) => o.trim()).filter(Boolean);
    if (!poll.question.trim() || opts.length < 2) { toast.error("Question + 2 options minimum"); return; }
    await send({ type: "poll", text: "", poll: { question: poll.question, options: opts, multi: poll.multi } });
    setPollDialog(false); setPoll({ question: "", options: ["", ""], multi: false });
  };
  const votePoll = async (mid, optionId) => {
    const { data } = await api.post(`/conversations/${active.id}/messages/${mid}/vote`, { option_id: optionId });
    setMessages((prev) => prev.map((mm) => mm.id === mid ? { ...mm, poll: data.poll } : mm));
  };
  const uploadAndSend = async (file, type) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/upload", fd);
    await send({ type, text: "", file: data });
  };
  const onPickFile = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    toast.promise(uploadAndSend(f, "file"), { loading: "Envoi…", success: "Fichier envoyé", error: "Échec" });
    e.target.value = "";
  };
  const sendPassword = async () => {
    if (!pwdValue.trim() && !pwdLogin.trim()) return;
    await send({ type: "password", text: pwdValue, login: pwdLogin.trim(), link: pwdLink.trim() });
    setPwdValue(""); setPwdLogin(""); setPwdLink(""); setPwdDialog(false);
    toast.success("Accès partagé (chiffré)");
  };
  const react = async (mid, emoji) => {
    const { data } = await api.post(`/conversations/${active.id}/messages/${mid}/react`, { emoji });
    setMessages((prev) => prev.map((mm) => mm.id === mid ? { ...mm, reactions: data.reactions } : mm));
  };
  const togglePin = async (c) => { setMenuFor(null); await api.post(`/conversations/${c.id}/pin`, { value: !c.pinned }); loadConvs(); };
  const toggleArchive = async (c) => {
    setMenuFor(null);
    await api.post(`/conversations/${c.id}/archive`, { value: !c.archived });
    if (active?.id === c.id) setActive(null);
    loadConvs();
  };

  // Audio recording
  const [recording, setRecording] = useState(false);
  const mediaRec = useRef(null); const chunks = useRef([]);
  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); mediaRec.current = mr; chunks.current = [];
      mr.ondataavailable = (e) => chunks.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        const file = new File([blob], `vocal-${Date.now()}.webm`, { type: "audio/webm" });
        toast.promise(uploadAndSend(file, "audio"), { loading: "Envoi du vocal…", success: "Vocal envoyé", error: "Échec" });
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start(); setRecording(true);
    } catch { toast.error("Micro inaccessible"); }
  };
  const stopRec = () => { mediaRec.current?.stop(); setRecording(false); };

  const visible = convs
    .filter((c) => (showArchived ? c.archived : !c.archived))
    .filter((c) => (filter === "unread" ? c.unread > 0 : filter === "pinned" ? c.pinned : true))
    .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  const archivedCount = convs.filter((c) => c.archived).length;

  return (
    <div className="flex h-full">
      {/* Conversation list */}
      <div className={`${active ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 lg:w-96 border-r border-white/10 bg-card`}>
        <div className="h-14 flex items-center justify-between px-4 border-b border-white/10">
          <span className="font-cabinet font-bold text-lg">Discussions</span>
          <button data-testid="new-conv-button" onClick={() => setShowNew(true)} className="p-2 rounded-md bg-klein text-zinc-950"><Plus size={16} /></button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 overflow-x-auto no-scrollbar">
          {FILTERS.map((f) => (
            <button key={f.id} data-testid={`chat-filter-${f.id}`} onClick={() => { setFilter(f.id); setShowArchived(false); }}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${!showArchived && filter === f.id ? "bg-klein text-zinc-950" : "bg-white/[0.06] text-zinc-400 hover:text-zinc-200"}`}>
              {f.label}{f.id === "unread" && convs.filter((c) => !c.archived && c.unread > 0).length > 0 ? ` ${convs.filter((c) => !c.archived && c.unread > 0).length}` : ""}
            </button>
          ))}
          {archivedCount > 0 && (
            <button data-testid="chat-filter-archived" onClick={() => setShowArchived((s) => !s)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1 transition-colors ${showArchived ? "bg-klein text-zinc-950" : "bg-white/[0.06] text-zinc-400 hover:text-zinc-200"}`}>
              <Archive size={12} /> Archivées {archivedCount}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {visible.length === 0 && <div className="p-12 text-center text-zinc-400 text-sm">{showArchived ? "Aucune discussion archivée." : "Démarrez une discussion."}</div>}
          {visible.map((c) => (
            <div key={c.id} data-testid={`conv-${c.id}`} onClick={() => openConv(c)}
              className={`group relative flex items-center gap-3 p-3.5 border-b border-white/[0.06] hover:bg-background cursor-pointer ${active?.id === c.id ? "bg-background" : ""}`}>
              <div className="relative shrink-0">
                {c.is_group
                  ? <div className="w-11 h-11 rounded-full bg-klein/20 text-klein flex items-center justify-center"><Users size={22} /></div>
                  : <Avatar name={c.other?.name} color={c.other?.avatar_color} src={c.other?.avatar_url} size={44} />}
                {!c.is_group && c.other_online && <span data-testid={`online-dot-${c.id}`} className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-card" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex justify-between items-center gap-2">
                  <span className="font-semibold text-sm truncate flex items-center gap-1">
                    {c.pinned && <Pin size={12} className="text-klein shrink-0 fill-klein" />}{c.is_group ? c.name : c.other?.name}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 truncate">{c.is_group ? `${c.member_count} membres` : ""}{typeof c.last_message === "string" && c.last_message ? (c.is_group ? " · " : "") + c.last_message : ""}</div>
                {c.is_widget
                  ? <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono-label px-1.5 py-0.5 rounded bg-sky-400/15 text-sky-500"><Globe size={9} />{c.origin_label}{c.assigned ? ` · ${c.assigned.name}` : " · à prendre"}</span>
                  : c.company_name && <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono-label px-1.5 py-0.5 rounded bg-klein/15 text-klein"><Building2 size={9} />{c.company_name} · Chat</span>}
              </div>
              {c.unread > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-signal text-white text-[11px] font-bold flex items-center justify-center">{c.unread}</span>}

              {/* Kebab menu */}
              <button data-testid={`conv-menu-${c.id}`} onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === c.id ? null : c.id); }}
                className="p-1.5 rounded-md hover:bg-white/10 text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity"><MoreVertical size={16} /></button>
              {menuFor === c.id && (
                <div className="absolute right-3 top-12 z-20 bg-background border border-white/10 rounded-lg py-1 w-40 shadow-xl" onClick={(e) => e.stopPropagation()}>
                  <button data-testid={`pin-conv-${c.id}`} onClick={() => togglePin(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-white/[0.06] flex items-center gap-2">
                    <Pin size={14} /> {c.pinned ? "Désépingler" : "Épingler"}
                  </button>
                  <button data-testid={`archive-conv-${c.id}`} onClick={() => toggleArchive(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-white/[0.06] flex items-center gap-2">
                    {c.archived ? <><ArchiveRestore size={14} /> Désarchiver</> : <><Archive size={14} /> Archiver</>}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Chat window */}
      <div className={`${active ? "flex" : "hidden md:flex"} flex-1 flex-col min-w-0 fd-chat-bg`}>
        {active ? (
          <>
            <div className="h-14 border-b border-white/10 bg-card flex items-center gap-3 px-4 shrink-0">
              <button onClick={() => setActive(null)} className="md:hidden p-1.5 rounded-md hover:bg-white/[0.06]"><ArrowLeft size={18} /></button>
              <div className="relative">
                {active.is_group
                  ? <div className="w-9 h-9 rounded-full bg-klein/20 text-klein flex items-center justify-center"><Users size={18} /></div>
                  : <Avatar name={active.other?.name} color={active.other?.avatar_color} src={active.other?.avatar_url} size={38} />}
                {!active.is_group && active.other_online && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-card" />}
              </div>
              <div>
                <div className="font-semibold text-sm">{active.is_group ? active.name : active.other?.name}</div>
                <div className="text-xs font-mono-label flex items-center gap-1.5">
                  {active.is_group
                    ? <span className="text-zinc-400">{(active.members || []).map((m) => m.name.split(" ")[0]).join(", ")}</span>
                    : active.is_widget
                      ? <span className="text-sky-500 flex items-center gap-1"><Globe size={11} />{active.origin_label}{active.other?.email ? ` · ${active.other.email}` : ""}</span>
                      : active.other_online
                        ? <span className="text-emerald-400">en ligne</span>
                        : <span className="text-zinc-400">{active.company_name ? `${active.company_name} · Chat` : active.other?.email}</span>}
                </div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden p-4 lg:p-6 space-y-1.5">
              {messages.map((m) => (
                <Bubble key={m.id} m={m} mine={m.sender_id === user.id} otherId={active.other?.id} myId={user.id} onReact={react} onVote={votePoll} isGroup={active.is_group} />
              ))}
              <div ref={bottomRef} />
              {typingFrom && (
                <div data-testid="typing-indicator" className="flex items-center gap-2 px-1 pb-1 text-xs text-zinc-400">
                  <span className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                  {typingFrom} est en train d'écrire…
                </div>
              )}
            </div>

            <div className="p-3 fd-chat-bg border-t border-white/10 flex items-end gap-2">
              <div className="flex-1 flex items-end gap-1 bg-card border border-white/10 rounded-3xl px-2 py-1 focus-within:border-klein/40 focus-within:ring-1 focus-within:ring-klein/25 transition-all">
                <button data-testid="attach-file-button" onClick={() => fileRef.current.click()} title="Joindre" className="p-2 rounded-full hover:bg-white/[0.06] text-zinc-500 shrink-0"><Paperclip size={19} /></button>
                <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} />
                <button data-testid="share-password-button" onClick={() => setPwdDialog(true)} title="Mot de passe sécurisé" className="p-2 rounded-full hover:bg-white/[0.06] text-zinc-500 shrink-0"><KeyRound size={19} /></button>
                <button data-testid="create-poll-button" onClick={() => setPollDialog(true)} title="Sondage" className="p-2 rounded-full hover:bg-white/[0.06] text-zinc-500 shrink-0"><BarChart3 size={19} /></button>
                <textarea data-testid="message-input" rows={1} value={text} onChange={(e) => { setText(e.target.value); pingTyping(); }}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
                  placeholder="Votre message…" className="flex-1 bg-transparent resize-none border-0 px-1 py-2 max-h-32 text-sm focus:outline-none focus:ring-0 placeholder:text-zinc-500" />
              </div>
              {text.trim() ? (
                <button data-testid="send-message-button" onClick={sendText} className="p-3 rounded-full bg-klein text-zinc-950 shrink-0 hover:brightness-110 transition-all shadow-lg"><Send size={19} /></button>
              ) : recording ? (
                <button data-testid="stop-recording-button" onClick={stopRec} className="p-3 rounded-full bg-signal text-white animate-pulse shrink-0"><Square size={19} /></button>
              ) : (
                <button data-testid="record-audio-button" onClick={startRec} className="p-3 rounded-full bg-klein text-zinc-950 shrink-0 hover:brightness-110 transition-all shadow-lg"><Mic size={19} /></button>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-zinc-300">
            <div className="w-16 h-16 rounded-2xl bg-white/[0.06] flex items-center justify-center"><Send size={28} className="text-zinc-300" /></div>
            <p className="mt-4 text-sm text-zinc-400">Sélectionnez une discussion</p>
          </div>
        )}
      </div>

      {/* New conversation / group dialog */}
      <Dialog open={showNew} onOpenChange={(o) => { setShowNew(o); if (!o) { setNewMode("direct"); setGroupName(""); setGroupMembers([]); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-cabinet">Nouvelle discussion</DialogTitle></DialogHeader>
          <div className="flex gap-1 p-1 bg-white/[0.06] rounded-md">
            <button data-testid="mode-direct" onClick={() => setNewMode("direct")} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-sm text-sm font-medium ${newMode === "direct" ? "bg-card" : "text-zinc-400"}`}><UserPlus size={15} /> Directe</button>
            <button data-testid="mode-group" onClick={() => setNewMode("group")} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-sm text-sm font-medium ${newMode === "group" ? "bg-card" : "text-zinc-400"}`}><Users size={15} /> Groupe</button>
          </div>
          {newMode === "group" && (
            <input data-testid="group-name-input" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Nom du groupe…"
              className="w-full border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          )}
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {users.map((u) => (
              newMode === "direct" ? (
                <button key={u.id} data-testid={`start-conv-${u.id}`} onClick={() => startConv(u)} className="w-full flex items-center gap-3 p-2.5 rounded-md hover:bg-background text-left">
                  <Avatar name={u.name} color={u.avatar_color} src={u.avatar_url} size={38} />
                  <div><div className="font-medium text-sm">{u.name}</div><div className="text-xs text-zinc-400 font-mono-label">{u.email}</div></div>
                </button>
              ) : (
                <button key={u.id} data-testid={`group-member-${u.id}`} onClick={() => toggleGroupMember(u.id)} className={`w-full flex items-center gap-3 p-2.5 rounded-md text-left border ${groupMembers.includes(u.id) ? "border-klein bg-klein/10" : "border-transparent hover:bg-background"}`}>
                  <Avatar name={u.name} color={u.avatar_color} src={u.avatar_url} size={38} />
                  <div className="flex-1"><div className="font-medium text-sm">{u.name}</div><div className="text-xs text-zinc-400 font-mono-label">{u.email}</div></div>
                  {groupMembers.includes(u.id) && <Check size={16} className="text-klein" />}
                </button>
              )
            ))}
          </div>
          {newMode === "group" && (
            <DialogFooter>
              <button data-testid="create-group-button" onClick={createGroup} disabled={!groupName.trim() || groupMembers.length === 0} className="px-4 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium flex items-center gap-2 disabled:opacity-50"><Users size={15} /> Créer le groupe ({groupMembers.length})</button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Poll dialog */}
      <Dialog open={pollDialog} onOpenChange={setPollDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><BarChart3 size={18} className="text-klein" /> Nouveau sondage</DialogTitle></DialogHeader>
          <input data-testid="poll-question" value={poll.question} onChange={(e) => setPoll({ ...poll, question: e.target.value })} placeholder="Votre question…"
            className="w-full border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          <div className="space-y-2">
            {poll.options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <input data-testid={`poll-option-${i}`} value={o} onChange={(e) => setPoll({ ...poll, options: poll.options.map((x, j) => j === i ? e.target.value : x) })} placeholder={`Option ${i + 1}`}
                  className="flex-1 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
                {poll.options.length > 2 && <button onClick={() => setPoll({ ...poll, options: poll.options.filter((_, j) => j !== i) })} className="p-2 rounded-md text-zinc-400 hover:text-signal"><X size={16} /></button>}
              </div>
            ))}
            <button data-testid="add-poll-option" onClick={() => setPoll({ ...poll, options: [...poll.options, ""] })} className="text-xs text-klein hover:underline flex items-center gap-1"><Plus size={13} /> Ajouter une option</button>
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
            <input type="checkbox" data-testid="poll-multi" checked={poll.multi} onChange={(e) => setPoll({ ...poll, multi: e.target.checked })} className="accent-klein" /> Autoriser plusieurs réponses
          </label>
          <DialogFooter>
            <button onClick={() => setPollDialog(false)} className="px-4 py-2 rounded-md border border-white/10 text-sm font-medium">Annuler</button>
            <button data-testid="send-poll-button" onClick={sendPoll} className="px-4 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium">Publier</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secure credentials dialog */}
      <Dialog open={pwdDialog} onOpenChange={setPwdDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><KeyRound size={18} className="text-klein" /> Partage sécurisé</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">Identifiant et mot de passe sont chiffrés sur le serveur et masqués dans la conversation.</p>
          <div className="space-y-2.5">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Identifiant</label>
              <input data-testid="share-login-input" type="text" value={pwdLogin} onChange={(e) => setPwdLogin(e.target.value)} placeholder="Identifiant / email de connexion" className="w-full border border-white/10 rounded-md px-3 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Mot de passe</label>
              <input data-testid="password-share-input" type="text" value={pwdValue} onChange={(e) => setPwdValue(e.target.value)} placeholder="Mot de passe ou secret…" className="w-full border border-white/10 rounded-md px-3 py-2.5 font-mono-label text-sm bg-background focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Lien de connexion <span className="text-zinc-600 normal-case">(optionnel)</span></label>
              <input data-testid="share-link-input" type="url" value={pwdLink} onChange={(e) => setPwdLink(e.target.value)} placeholder="https://exemple.com/login" className="w-full border border-white/10 rounded-md px-3 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setPwdDialog(false)} className="px-4 py-2 rounded-md border border-white/10 text-sm font-medium">Annuler</button>
            <button data-testid="confirm-share-password" onClick={sendPassword} className="px-4 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium">Partager</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Bubble({ m, mine, otherId, myId, onReact, onVote, isGroup }) {
  const [reveal, setReveal] = useState(false);
  const [secs, setSecs] = useState(30);
  const [showPicker, setShowPicker] = useState(false);
  useEffect(() => {
    if (!reveal) return;
    setSecs(30);
    const t = setInterval(() => setSecs((s) => Math.max(0, s - 1)), 1000);
    const h = setTimeout(() => setReveal(false), 30000);
    return () => { clearInterval(t); clearTimeout(h); };
  }, [reveal]);

  if (m.is_system || m.sender_id === "system") {
    return (
      <div className="flex justify-center my-1" data-testid={`chat-system-${m.id}`}>
        <div className="max-w-[88%] fd-system-msg rounded-xl bg-emerald-500/15 border border-emerald-600/40 text-emerald-200 text-xs px-3.5 py-2.5 whitespace-pre-wrap leading-relaxed font-medium">{m.text}</div>
      </div>
    );
  }

  const base = mine ? "fd-bubble-out rounded-2xl rounded-tr-sm" : "bg-card border border-white/10 text-zinc-100 rounded-2xl rounded-tl-sm";
  const read = mine && otherId && (m.read_by || []).includes(otherId);
  const reactions = m.reactions || {};
  const reactionEntries = Object.entries(reactions).filter(([, arr]) => arr && arr.length);
  const poll = m.poll;
  const totalVotes = poll ? poll.options.reduce((s, o) => s + (o.votes?.length || 0), 0) : 0;

  return (
    <div className={`group/bubble flex flex-col ${mine ? "items-end" : "items-start"}`}>
      <div className="relative">
        <div className={`max-w-[80%] sm:max-w-[60%] p-2.5 px-3 shadow-sm animate-slide-up ${base}`}>
          {isGroup && !mine && m.type !== "password" && <div className="text-[11px] font-bold text-klein mb-0.5">{m.sender_name}</div>}
          {m.type === "text" && <span className="whitespace-pre-wrap text-sm">{m.text}</span>}
          {m.type === "audio" && <audio controls src={fileUrl(m.file?.id)} className="w-[240px] max-w-full h-10" />}
          {m.type === "file" && (
            <a href={fileUrl(m.file?.id)} target="_blank" rel="noreferrer" className={`flex items-center gap-2 text-sm ${mine ? "" : "text-klein"}`}>
              <FileText size={18} /> <span className="truncate max-w-[160px]">{m.file?.name}</span>
            </a>
          )}
          {m.type === "poll" && poll && (
            <div data-testid={`poll-${m.id}`} className="min-w-[220px]">
              <div className="flex items-center gap-1.5 text-sm font-semibold mb-2"><BarChart3 size={15} /> {poll.question}</div>
              <div className="space-y-1.5">
                {poll.options.map((o) => {
                  const count = o.votes?.length || 0;
                  const pct = totalVotes ? Math.round((count / totalVotes) * 100) : 0;
                  const voted = (o.votes || []).includes(myId);
                  return (
                    <button key={o.id} data-testid={`poll-opt-${o.id}`} onClick={() => onVote(m.id, o.id)}
                      className={`relative w-full text-left rounded-md overflow-hidden border ${voted ? (mine ? "border-zinc-900" : "border-klein") : (mine ? "border-zinc-900/30" : "border-white/10")}`}>
                      <div className={`absolute inset-0 ${mine ? "bg-zinc-900/15" : "bg-klein/15"}`} style={{ width: `${pct}%` }} />
                      <div className="relative flex items-center justify-between px-2.5 py-1.5 text-xs">
                        <span className="flex items-center gap-1.5">{voted && <Check size={13} />}{o.text}</span>
                        <span className="font-mono-label opacity-80">{pct}%</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className={`text-[10px] mt-1.5 ${mine ? "tick-time" : "text-zinc-400"}`}>{totalVotes} vote{totalVotes > 1 ? "s" : ""}{poll.multi ? " · choix multiples" : ""}</div>
            </div>
          )}
          {m.type === "password" && (
            <div className="min-w-[210px] space-y-1.5" data-testid={`secure-share-${m.id}`}>
              <div className="flex items-center gap-1.5 text-xs font-semibold opacity-80"><KeyRound size={14} className={mine ? "" : "text-klein"} /> Accès sécurisé</div>
              {m.login ? (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider opacity-60 w-14 shrink-0">Ident.</span>
                  <span className="font-mono-label text-sm truncate flex-1">{m.login}</span>
                  <button data-testid={`copy-login-${m.id}`} onClick={() => { navigator.clipboard.writeText(m.login); toast.success("Identifiant copié"); }} className="opacity-70 hover:opacity-100 shrink-0"><Copy size={13} /></button>
                </div>
              ) : null}
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider opacity-60 w-14 shrink-0">Mot de p.</span>
                <span className="font-mono-label text-sm tracking-wider flex-1 truncate">{reveal ? m.text : "••••••••••"}</span>
                {reveal && <span className="font-mono-label text-[10px] px-1.5 py-0.5 rounded-full bg-black/20 shrink-0">{secs}s</span>}
                <button data-testid="reveal-password" onClick={() => setReveal((r) => !r)} className="opacity-70 hover:opacity-100 shrink-0" title="Visible 30s">{reveal ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                <button data-testid={`copy-password-${m.id}`} onClick={() => { navigator.clipboard.writeText(m.text); toast.success("Mot de passe copié"); }} className="opacity-70 hover:opacity-100 shrink-0"><Copy size={14} /></button>
              </div>
              {m.link ? (
                <a data-testid={`secure-link-${m.id}`} href={m.link} target="_blank" rel="noreferrer" className={`flex items-center gap-2 text-xs font-medium hover:underline ${mine ? "" : "text-klein"}`}>
                  <span className="text-[10px] uppercase tracking-wider opacity-60 w-14 shrink-0">Lien</span>
                  <span className="truncate flex-1">{m.link}</span>
                  <ExternalLink size={13} className="shrink-0 opacity-70" />
                </a>
              ) : null}
            </div>
          )}
          <div className={`flex items-center gap-1 justify-end mt-1 ${mine ? "tick-time" : "text-zinc-400"}`}>
            <span className="text-[10px] font-mono-label tick-time">{new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
            {mine && (read
              ? <CheckCheck size={14} style={{ color: "#53bdeb" }} data-testid={`tick-read-${m.id}`} />
              : <Check size={14} data-testid={`tick-sent-${m.id}`} />)}
          </div>
        </div>

        {/* Reaction trigger */}
        <button data-testid={`react-btn-${m.id}`} onClick={() => setShowPicker((s) => !s)}
          className={`absolute top-1/2 -translate-y-1/2 ${mine ? "-left-8" : "-right-8"} p-1.5 rounded-full bg-card border border-white/10 text-zinc-400 opacity-0 group-hover/bubble:opacity-100 transition-opacity hover:text-klein`}>
          <Smile size={14} />
        </button>
        {showPicker && (
          <div className={`absolute z-20 -top-9 ${mine ? "right-0" : "left-0"} bg-background border border-white/10 rounded-full px-2 py-1 flex gap-1 shadow-xl`}>
            {REACTIONS.map((e) => (
              <button key={e} data-testid={`react-${e}-${m.id}`} onClick={() => { onReact(m.id, e); setShowPicker(false); }} className="text-base hover:scale-125 transition-transform">{e}</button>
            ))}
          </div>
        )}
      </div>

      {/* Reactions chips */}
      {reactionEntries.length > 0 && (
        <div className={`flex gap-1 mt-0.5 ${mine ? "mr-1" : "ml-1"}`}>
          {reactionEntries.map(([emoji, arr]) => (
            <button key={emoji} data-testid={`reaction-chip-${emoji}-${m.id}`} onClick={() => onReact(m.id, emoji)}
              className={`flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded-full border ${arr.includes(myId) ? "bg-klein/20 border-klein/40 text-klein" : "bg-card border-white/10 text-zinc-300"}`}>
              {emoji} {arr.length > 1 && <span className="font-mono-label">{arr.length}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
